import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Skeleton } from '@/ui/base/skeleton';
import { ArrowLeft } from 'lucide-react';

import { useLancamentoOs, useSalvarLancamentoOs } from '../hooks/useApuracao';
import type { LancamentoOsFormulario } from '../types';

/** Mês (AAAA-MM) por extenso, para o cabeçalho do lançamento. */
function formatarMesLabel(mes: string): string {
  const [ano, mesNum] = mes.split('-').map(Number);
  if (!ano || !mesNum) return mes;
  const texto = new Date(ano, mesNum - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** FIN-062: lançamento de uma OS na competência — identificação somente
 * leitura, frequência, quilometragem, atividades complementares, atendimentos
 * e ajustes. */
export function LancamentoOsPage() {
  const { osId = '', mes = '' } = useParams<{ osId: string; mes: string }>();
  const navigate = useNavigate();
  const voltarPara = `/operacao/financeiro/apuracao-mensal/${mes}`;

  const { data, isLoading, isError, refetch } = useLancamentoOs(osId, mes);
  const salvar = useSalvarLancamentoOs(osId, mes);

  const [form, definirForm] = useState<LancamentoOsFormulario | null>(null);

  useEffect(() => {
    if (data) definirForm(data);
  }, [data]);

  const diasFalta = form ? Math.max(form.diasUteis - form.diasOperados, 0) : 0;
  const mesLabel = mes ? formatarMesLabel(mes) : '';

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(voltarPara)}>
        <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Voltar
      </Button>

      <PageHeader
        eyebrow="Financeiro · Apuração"
        title={form ? `Lançamento — OS ${form.numeroOs}` : 'Lançamento de OS'}
        description={
          form
            ? `${mesLabel} • ${form.credenciadoNome} — os campos abaixo ainda não gravam na API real.`
            : 'Etapa futura da migração: os campos abaixo ainda não gravam na API real.'
        }
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : !form ? (
        <ErrorState variant="not-found" title="OS não encontrada nesta competência" />
      ) : (
        <Card>
          <CardContent className="space-y-6 p-6">
            <section className="grid gap-4 sm:grid-cols-2">
              <h3 className="col-span-full font-medium">Identificação</h3>
              <div>
                <Label>Nº OS</Label>
                <Input value={form.numeroOs} disabled />
              </div>
              <div>
                <Label>Tipo TEG</Label>
                <Input value={form.tipoTeg} disabled />
              </div>
              <div>
                <Label>Credenciado</Label>
                <Input value={form.credenciadoNome} disabled />
              </div>
              <div>
                <Label>Km Rota Base</Label>
                <Input value={form.kmRotaBase} disabled />
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-3">
              <h3 className="col-span-full font-medium">Frequência</h3>
              <div>
                <Label>Dias Úteis</Label>
                <Input value={form.diasUteis} disabled />
              </div>
              <div>
                <Label htmlFor="dias-operados">Dias Operados</Label>
                <Input
                  id="dias-operados"
                  type="number"
                  min={0}
                  max={form.diasUteis}
                  value={form.diasOperados}
                  onChange={(e) => definirForm({ ...form, diasOperados: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label>Dias Falta</Label>
                <Input value={diasFalta} disabled className="text-destructive" />
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <h3 className="col-span-full font-medium">Quilometragem</h3>
              <div>
                <Label htmlFor="km-rodados">Km Rodados</Label>
                <Input
                  id="km-rodados"
                  value={form.kmRodados}
                  onChange={(e) => definirForm({ ...form, kmRodados: e.target.value })}
                />
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-3">
              <h3 className="col-span-full font-medium">Atividades Complementares</h3>
              <div>
                <Label htmlFor="ativ-presencial">Presencial</Label>
                <Input
                  id="ativ-presencial"
                  type="number"
                  min={0}
                  value={form.ativPresencial}
                  onChange={(e) => definirForm({ ...form, ativPresencial: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="ativ-remoto">Remoto</Label>
                <Input
                  id="ativ-remoto"
                  type="number"
                  min={0}
                  value={form.ativRemoto}
                  onChange={(e) => definirForm({ ...form, ativRemoto: Number(e.target.value) })}
                />
              </div>
              <div>
                <Label htmlFor="ativ-externa">Externa</Label>
                <Input
                  id="ativ-externa"
                  type="number"
                  min={0}
                  value={form.ativExterna}
                  onChange={(e) => definirForm({ ...form, ativExterna: Number(e.target.value) })}
                />
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <h3 className="col-span-full font-medium">Atendimentos</h3>
              <div>
                <Label htmlFor="atend-cad">Cadeirante</Label>
                <Input
                  id="atend-cad"
                  type="number"
                  min={0}
                  value={form.atendimentosCadeirante}
                  onChange={(e) =>
                    definirForm({ ...form, atendimentosCadeirante: Number(e.target.value) })
                  }
                />
              </div>
              <div>
                <Label htmlFor="atend-ncad">Não Cadeirante</Label>
                <Input
                  id="atend-ncad"
                  type="number"
                  min={0}
                  value={form.atendimentosNaoCadeirante}
                  onChange={(e) =>
                    definirForm({ ...form, atendimentosNaoCadeirante: Number(e.target.value) })
                  }
                />
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <h3 className="col-span-full font-medium">Ajustes</h3>
              <div>
                <Label htmlFor="ajuste-valor">Valor Ajuste (R$)</Label>
                <Input
                  id="ajuste-valor"
                  value={form.ajusteValor}
                  onChange={(e) => definirForm({ ...form, ajusteValor: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="ajuste-descricao">Descrição</Label>
                <Input
                  id="ajuste-descricao"
                  value={form.ajusteDescricao}
                  onChange={(e) => definirForm({ ...form, ajusteDescricao: e.target.value })}
                />
              </div>
            </section>

            <section>
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={form.observacoes}
                onChange={(e) => definirForm({ ...form, observacoes: e.target.value })}
                rows={3}
              />
            </section>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate(voltarPara)}>
                Cancelar
              </Button>
              <Button
                disabled={salvar.isPending}
                onClick={() => {
                  salvar.mutate(form as unknown as Record<string, unknown>, {
                    onSuccess: () => navigate(voltarPara),
                  });
                }}
              >
                {salvar.isPending ? 'Salvando…' : 'Salvar Lançamento'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default LancamentoOsPage;
